/**
 * Local preview harness. Answers every API call from fixtures and pre-seeds the auth token, so the CMS
 * can be opened and looked at without the backend and without signing in:
 *
 *   VUE_APP_MOCK=1 npm run serve
 *
 * Guarded by the env flag, so a normal build never reaches the fixtures. Import it first in main.js -
 * store modules read localStorage while they are being imported.
 */
import axios from 'axios'

const PLACE = {
  id: 1,
  name: 'BergTechnology',
  type: 'DIGITAL_SIGNAGE',
  subPlaces: [],
  devices: []
}

const USER = {
  id: 1,
  username: 'dev@bergregions.pl',
  email: 'dev@bergregions.pl',
  firstName: 'Podgląd',
  lastName: 'Lokalny',
  type: 'ADMIN',
  authority: 'ADMIN',
  apps: [],
  roles: ['ROLE_DS_APP_FULL_MANAGEMENT', 'ROLE_DS_APP_PUBLICATION', 'ROLE_DS_APP_VIEW', 'ROLE_TV_APP_FULL_MANAGEMENT', 'ROLE_TV_APP_PUBLICATION', 'ROLE_TV_APP_VIEW', 'ROLE_APP_FULL_MANAGEMENT', 'ROLE_DEVICE_FULL_MANAGEMENT', 'ROLE_DEVICE_VIEW', 'ROLE_USER_FULL_MANAGEMENT', 'ROLE_USER_VIEW']
}

const APPS = [
  { id: 11, name: 'Menu Board — śniadania', status: 'PUBLISHED', type: 'DIGITAL_SIGNAGE' },
  { id: 12, name: 'Menu Board — obiady', status: 'PUBLISHED', type: 'DIGITAL_SIGNAGE' },
  { id: 13, name: 'Ski Bar — wieczór', status: 'PUBLISHED', type: 'DIGITAL_SIGNAGE' },
  { id: 14, name: 'Powitanie gości', status: 'PUBLISHED', type: 'DIGITAL_SIGNAGE' },
  { id: 15, name: 'Szkic — nieopublikowany', status: 'DRAFT', type: 'DIGITAL_SIGNAGE' }
]

const DEVICES = [
  { mac: '00:1A:2B:3C:4D:01', name: 'Recepcja — hol', ip: '10.10.4.11', deviceType: 'DIGITAL_SIGNAGE' },
  { mac: '00:1A:2B:3C:4D:02', name: 'Restauracja — wejście', ip: '10.10.4.12', deviceType: 'DIGITAL_SIGNAGE' },
  { mac: '00:1A:2B:3C:4D:03', name: 'Ski Bar — nad barem', ip: '10.10.4.13', deviceType: 'DIGITAL_SIGNAGE' },
  { mac: '00:1A:2B:3C:4D:04', name: 'Wypożyczalnia', ip: '10.10.4.14', deviceType: 'DIGITAL_SIGNAGE' },
  { mac: '00:1A:2B:3C:4D:05', name: 'Basen — szatnia', ip: '10.10.4.15', deviceType: 'DIGITAL_SIGNAGE' },
  { mac: '00:1A:2B:3C:4D:06', name: 'Pokój 210 (Hotel TV)', ip: '10.10.4.16', deviceType: 'HOTEL_TV' }
]

const scheduleDevice = (mac, currentTvAppId, currentTvAppName, fromSchedule, nextChangeAt, servedTvAppId) => {
  const device = DEVICES.find((item) => item.mac === mac)

  return {
    id: DEVICES.indexOf(device) + 1,
    deviceId: DEVICES.indexOf(device) + 1,
    mac: device.mac,
    name: device.name,
    ip: device.ip,
    currentTvAppId,
    currentTvAppName,
    fromSchedule,
    nextChangeAt,
    servedTvAppId: servedTvAppId === undefined ? currentTvAppId : servedTvAppId
  }
}

const SCHEDULES = [
  {
    id: 1,
    name: 'Restauracja — dzień roboczy',
    placeId: PLACE.id,
    timeZone: 'Europe/Warsaw',
    validFrom: '2026-08-01',
    validTo: null,
    enabled: true,
    windows: [
      { id: 101, tvAppId: 11, tvAppName: 'Menu Board — śniadania', startTime: '07:00', endTime: '11:00', daysOfWeek: [1, 2, 3, 4, 5] },
      { id: 102, tvAppId: 12, tvAppName: 'Menu Board — obiady', startTime: '11:30', endTime: '16:00', daysOfWeek: [1, 2, 3, 4, 5] },
      { id: 103, tvAppId: 13, tvAppName: 'Ski Bar — wieczór', startTime: '17:00', endTime: '23:00', daysOfWeek: [5, 6] },
      { id: 104, tvAppId: 14, tvAppName: 'Powitanie gości', startTime: '09:00', endTime: '20:00', daysOfWeek: [6, 7] }
    ],
    devices: [scheduleDevice('00:1A:2B:3C:4D:01', 12, 'Menu Board — obiady', true, '2026-08-24T16:00:00+02:00'), scheduleDevice('00:1A:2B:3C:4D:02', 12, 'Menu Board — obiady', true, '2026-08-24T16:00:00+02:00', 11), scheduleDevice('00:1A:2B:3C:4D:03', 14, 'Powitanie gości', false, null)],
    exceptions: [
      { id: 201, date: '2026-08-26', tvAppId: 14, tvAppName: 'Powitanie gości', note: 'Wesele w sali kominkowej' },
      { id: 202, date: '2026-09-01', tvAppId: null, tvAppName: null, note: 'Przegląd instalacji' }
    ]
  },
  {
    id: 2,
    name: 'Test',
    placeId: PLACE.id,
    timeZone: 'Europe/Warsaw',
    validFrom: '2026-08-24',
    validTo: '2026-08-24',
    enabled: false,
    windows: [],
    devices: [],
    exceptions: []
  }
]

/** Ordered - the first pattern that matches the URL wins. */
const ROUTES = [
  [/\/api\/v1\/user-profile$/, () => USER],
  [/\/api\/v1\/place-structure$/, () => [PLACE]],
  [/\/api\/v1\/tv-app\/all\//, () => APPS],
  [/\/api\/v2\/cms\/device\/all\//, () => DEVICES],
  [/\/api\/v1\/tv-app-schedule\/place\//, () => SCHEDULES],
  [/\/api\/v1\/tv-app-schedule\/(\d+)$/, (match) => SCHEDULES.find((schedule) => schedule.id === Number(match[1])) || SCHEDULES[0]]
]

function respond(config) {
  const url = config.url || ''

  for (let index = 0; index < ROUTES.length; index++) {
    const match = url.match(ROUTES[index][0])
    if (match) return ROUTES[index][1](match)
  }

  console.warn('[devMock] no fixture for', config.method, url)
  return []
}

/**
 * With a token in VUE_APP_DEV_TOKEN the harness talks to the real API instead of the fixtures - the same
 * screens, real content. The token stays in the environment; it is never read from a file in the repo.
 *
 *   VUE_APP_MOCK=1 VUE_APP_DEV_TOKEN=<jwt> VUE_APP_DEV_PLACE='{"id":1,"name":"…"}' npm run serve
 */
if (process.env.VUE_APP_MOCK === '1') {
  const live = !!process.env.VUE_APP_DEV_TOKEN

  localStorage.setItem('auth_token_default', live ? process.env.VUE_APP_DEV_TOKEN : 'dev-mock-token')
  localStorage.setItem('currentWorkspace', live ? process.env.VUE_APP_DEV_PLACE || JSON.stringify(PLACE) : JSON.stringify(PLACE))

  // The router guard reads user roles synchronously on the first navigation, long before $auth.fetch()
  // resolves - without seeding them here every route lands on /redirect. They only open the local screens;
  // the API still answers to the real roles behind the token. require, not import: the store reads the
  // localStorage seeded above while it is being evaluated.
  require('@/store').default.commit('user/setUserData', USER)

  if (!live) {
    axios.defaults.adapter = (config) =>
      new Promise((resolve) => {
        const data = respond(config)
        setTimeout(() => resolve({ data, status: 200, statusText: 'OK', headers: {}, config }), 60)
      })
  }
}
